import { useState } from 'react';
import { Video, FlaskConical, Briefcase } from 'lucide-react';
import type { CreateTopicRequest } from '@/lib/api/trainer';
import { RichTextEditor } from '@/components/RichTextEditor';
import { isRichTextEmpty } from '@/components/RichText';

type TopicType = 'lecture' | 'lab' | 'project';

const TOPIC_TYPES: { value: TopicType; label: string; icon: React.ElementType; description: string }[] = [
  { value: 'lecture', label: 'Lecture', icon: Video, description: 'Theory or concept session' },
  { value: 'lab', label: 'Lab', icon: FlaskConical, description: 'Hands-on practice' },
  { value: 'project', label: 'Project', icon: Briefcase, description: 'Build something end-to-end' },
];

interface Props {
  initial?: { title: string; topic_type: TopicType; estimated_hours?: number; description?: string };
  onSave: (req: CreateTopicRequest) => Promise<void>;
  onCancel: () => void;
}

const inputClass = 'w-full rounded-md border border-[#ececec] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const labelClass = 'mb-1.5 block text-xs font-medium text-[#6b7280]';

export function TopicForm({ initial, onSave, onCancel }: Props) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [topicType, setTopicType] = useState<TopicType>(initial?.topic_type ?? 'lecture');
  const [hours, setHours] = useState(initial?.estimated_hours?.toString() ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave({
        title: title.trim(),
        description: isRichTextEmpty(description) ? '' : description,
        topic_type: topicType,
        sort_order: 0,
        estimated_hours: hours ? parseFloat(hours) : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Type picker */}
      <div>
        <label className={labelClass}>Topic type *</label>
        <div className="grid grid-cols-3 gap-2">
          {TOPIC_TYPES.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setTopicType(t.value)}
                className={`flex flex-col items-center gap-1.5 rounded-md border px-2 py-3 text-sm transition-colors ${
                  topicType === t.value
                    ? 'border-orange-400 bg-orange-50 text-orange-700 dark:bg-orange-950/30 dark:text-orange-400'
                    : 'border-[#ececec] text-[#6b7280] hover:bg-[#fafafa]'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="font-semibold text-xs">{t.label}</span>
                <span className="text-[10px] opacity-70 text-center leading-tight">{t.description}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Title */}
      <div>
        <label className={labelClass}>Title *</label>
        <input
          required
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={
            topicType === 'lecture' ? 'e.g. Introduction to React Hooks' :
            topicType === 'lab' ? 'e.g. Build a Todo App with useState' :
            'e.g. Full Stack Auth System'
          }
          className={inputClass}
        />
      </div>

      {/* Description */}
      <div>
        <label className={labelClass}>About this topic <span className="text-[#9ca3af]">(shown to students)</span></label>
        <RichTextEditor
          value={description}
          onChange={setDescription}
          placeholder="What this topic covers, why it matters, what students will learn…"
        />
      </div>

      {/* Estimated hours */}
      <div>
        <label className={labelClass}>Estimated hours <span className="text-[#9ca3af]">(optional)</span></label>
        <input
          type="number"
          min="0.25"
          step="0.25"
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          placeholder="e.g. 2"
          className={inputClass}
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-[#ececec] px-4 py-2 text-sm hover:bg-[#fafafa] transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy || !title.trim()}
          className="rounded-md bg-[#ff5a1f] px-4 py-2 text-sm font-medium text-white disabled:opacity-50 transition-colors"
        >
          {busy ? 'Saving…' : initial ? 'Update topic' : 'Add topic'}
        </button>
      </div>
    </form>
  );
}
